import { ParcDto, RealEstateDto } from "api/real-estate";
import { NsKey } from "enums/NsKey";
import { Building2, CheckCircle2, Home, Maximize2, MinusCircle } from "lucide-react";
import { FC, useMemo } from "react";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import {
  formatAddress,
  formatEuroPrice,
  formatPropertyType,
} from "features/subscription-process/home-insurance/utils/realEstateFormatters";

interface ContractParcOverviewProps {
  parc: ParcDto;
  /** Ids des biens du parc effectivement couverts par le contrat (≥ 1 garantie active). */
  coveredRealEstateIds: string[];
}

const Stat: FC<{ icon: React.ReactNode; value: string; label: string }> = ({ icon, value, label }) => (
  <div className="flex flex-col items-center gap-0.5 grow">
    <span className="text-gray-400">{icon}</span>
    <span className="text-lg font-semibold text-gray-900">{value}</span>
    <span className="text-xs text-gray-500">{label}</span>
  </div>
);

/**
 * Vue lecture seule du parc couvert par un contrat (risque parc GLI/CBNO) :
 * en-tête + stats globales, puis la liste des biens avec mise en évidence
 * de ceux couverts par le contrat.
 */
export const ContractParcOverview: FC<ContractParcOverviewProps> = ({ parc, coveredRealEstateIds }) => {
  const { t } = useDynamicTranslation([NsKey.RealEstate, NsKey.Common]);
  const covered = useMemo(() => new Set(coveredRealEstateIds), [coveredRealEstateIds]);

  const biens = parc.realEstates ?? [];
  // Biens couverts en tête de liste.
  const sortedBiens = useMemo(
    () => [...biens].sort((a, b) => Number(covered.has(b.id)) - Number(covered.has(a.id))),
    [biens, covered],
  );

  const totalSurface = biens.reduce((sum, b) => sum + (b.area ?? 0), 0);
  const totalRent = biens.reduce((sum, b) => sum + (b.rentAmount ?? 0), 0);

  const renderCoverageBadge = (bien: RealEstateDto) =>
    covered.has(bien.id) ? (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border bg-green-100 text-green-700 border-green-300">
        <CheckCircle2 size={12} />
        Couvert
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border bg-gray-100 text-gray-500 border-gray-300">
        <MinusCircle size={12} />
        Non couvert
      </span>
    );

  return (
    <div className="space-y-6">
      {/* En-tête parc */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Building2 className="text-primary" size={20} />
          </div>
          <div className="flex flex-col min-w-0">
            <h3 className="font-medium text-gray-900 truncate">{parc.name}</h3>
            <span className="text-xs text-gray-400">{parc.reference}</span>
          </div>
        </div>
        <span className="px-3 py-1 rounded-full text-xs font-medium border bg-green-100 text-green-700 border-green-300">
          {covered.size} {covered.size > 1 ? "biens couverts" : "bien couvert"} sur {biens.length}
        </span>
      </div>

      {/* Stats globales */}
      <div className="flex items-center border rounded-xl bg-white shadow-sm p-4">
        <Stat icon={<Home size={16} />} value={`${biens.length}`} label={biens.length > 1 ? "biens" : "bien"} />
        <div className="border-l h-10" />
        <Stat icon={<Maximize2 size={16} />} value={`${totalSurface} m²`} label="surface totale" />
        {totalRent > 0 && (
          <>
            <div className="border-l h-10" />
            <Stat
              icon={<span className="text-sm font-semibold">€</span>}
              value={formatEuroPrice(totalRent)}
              label="loyer total / mois"
            />
          </>
        )}
      </div>

      {/* Liste des biens */}
      <div className="overflow-x-auto border rounded-xl bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b bg-gray-50/50">
              <th className="px-4 py-3 font-medium">Bien</th>
              <th className="px-4 py-3 font-medium">Adresse</th>
              <th className="px-4 py-3 font-medium">Type</th>
              <th className="px-4 py-3 font-medium text-right">Surface</th>
              <th className="px-4 py-3 font-medium text-right">Loyer / mois</th>
              <th className="px-4 py-3 font-medium text-center">Couverture</th>
            </tr>
          </thead>
          <tbody>
            {sortedBiens.map((bien) => {
              const isCovered = covered.has(bien.id);
              return (
                <tr
                  key={bien.id}
                  className={`border-b last:border-b-0 ${isCovered ? "" : "opacity-60"}`}
                >
                  <td className="px-4 py-3 font-medium text-gray-900">{bien.name || "—"}</td>
                  <td className="px-4 py-3 text-gray-700">{formatAddress(bien.address) || "—"}</td>
                  <td className="px-4 py-3 text-gray-700">{formatPropertyType(bien.propertyType, t) || "—"}</td>
                  <td className="px-4 py-3 text-gray-700 text-right">{bien.area ? `${bien.area} m²` : "—"}</td>
                  <td className="px-4 py-3 text-gray-700 text-right">
                    {bien.rentAmount ? formatEuroPrice(bien.rentAmount) : "—"}
                  </td>
                  <td className="px-4 py-3 text-center">{renderCoverageBadge(bien)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
